/**
 * ModelingNode — card in the build-phase DAG. (v5 "compact" restyle)
 *
 * v5 anatomy (42px tall, 184px wide):
 *   ┌─┬────────────────────────────────────┐
 *   │ │ INPUT DATA            ● [warn pin] │  ← 3px left accent bar + small
 *   │ │ Segment Daily Balances…            │    category label; 1-line title
 *   └─┴────────────────────────────────────┘    (ellipsis, full name on hover)
 *
 * Changes from the airy version:
 *   • MODELING_NODE_HEIGHT 96 → 42, width 240 → 184
 *   • filled pill  →  left category accent bar + small uppercase label
 *   • title 14px / 2-line clamp  →  12px / single-line ellipsis
 *   • engine glyph removed (re-add if product wants the Py/Sheets/Jupyter mark)
 *
 * Colours still come from NODE_TYPE_COLORS in types/modeling.ts.
 */

import React, { memo, useContext } from 'react';
import { Handle, Position } from 'reactflow';
import type { NodeProps } from 'reactflow';
import { AlertCircle, Database } from 'lucide-react';
import { HighlightContext } from '../shared/HighlightContext';
import type { ModelingNodeType, EngineType, RunStatus } from '../../types/modeling';
import { getNodeTypeColor, getNodeTypeLabel } from '../../types/modeling';
import { decodeEntities } from '../../utils/planGraphData';

// Fixed card height + width — single source of truth shared with the build-view
// layout: ProcessDAG hands these to ELK as the node size, and the tier-lane bar
// wraps `MODELING_NODE_HEIGHT + 2*pad`. Content is vertically centered so the
// card, its ELK slot, and its tier bar always agree.
export const MODELING_NODE_HEIGHT = 42;
export const MODELING_NODE_WIDTH = 184;

export interface ModelingNodeData {
  nodeType: ModelingNodeType;
  engineType?: EngineType;  // retained in the data contract; not rendered in v5
  label: string;
  runStatus?: RunStatus;
  executionTimeMs?: number;
  validationSummary?: { pass: number; fail: number };
  inputCount?: number;
  outputCount?: number;
  /** INPUT_DATA only: true = a file is uploaded (data ready), false = required
   *  but missing, undefined = not an input-data node (no badge). Owner #4. */
  inputDataReady?: boolean;
}

/** Map RunStatus → visual description for the status dot. */
function statusView(s: RunStatus | undefined): { label: string; dotClass: string; pulse: boolean } | null {
  switch (s) {
    case 'running':   return { label: 'Running',   dotClass: 'bg-warning',     pulse: true  };
    case 'completed': return { label: 'Completed', dotClass: 'bg-success',     pulse: false };
    case 'failed':    return { label: 'Failed',    dotClass: 'bg-danger',      pulse: false };
    case 'pending':   return { label: 'Pending',   dotClass: 'bg-fg-tertiary', pulse: false };
    default:          return null;
  }
}

function ModelingNodeComponent({ data, selected, id }: NodeProps<ModelingNodeData>) {
  const { nodeType, runStatus, validationSummary } = data;
  // Backend stores node names HTML-escaped (e.g. "Pool summary &amp; …"); the DAG
  // renders the label as plain text, so decode at this view boundary — same
  // convention as planGraphData.ts ("&amp;" decoded LAST).
  const label = decodeEntities(data.label);

  const color = getNodeTypeColor(nodeType);
  const typeLabel = getNodeTypeLabel(nodeType).toUpperCase();
  const status = statusView(runStatus);

  const hasValidationIssue = !!validationSummary && validationSummary.fail > 0;
  const validationLabel = validationSummary
    ? validationSummary.fail === 0
      ? `${validationSummary.pass}/${validationSummary.pass + validationSummary.fail} passing`
      : `${validationSummary.fail} failing`
    : null;

  // Path-highlight dim
  const highlight = useContext(HighlightContext);
  const isDimmed = highlight.active && !highlight.nodeIds.has(id);

  const cardStyle: React.CSSProperties = {
    height: MODELING_NODE_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    background: 'var(--surface)',
    borderColor: selected ? color : 'var(--border-default)',
    // v5: 3px category accent bar on the left edge (replaces the filled pill).
    borderLeft: `3px solid ${color}`,
    boxShadow: selected
      ? `0 0 0 3px color-mix(in srgb, ${color} 18%, transparent), var(--shadow-md)`
      : 'var(--shadow-xs)',
  };

  return (
    <div
      className="relative group"
      style={{ opacity: isDimmed ? 0.3 : 1, transition: 'opacity 0.3s ease' }}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2 !h-2 !border-2 !border-surface !-top-1"
        style={{ backgroundColor: 'var(--border-strong)' }}
      />

      {/* Card */}
      <div
        className={`
          relative w-[184px] rounded-md border px-[11px] py-[6px] transition-all duration-200
          hover:-translate-y-px cursor-pointer overflow-hidden
          ${runStatus === 'running' ? 'animate-pulse' : ''}
        `}
        style={cardStyle}
        title={label}
      >
        {/* Row 1: small category label + status dot + validation pin */}
        <div className="flex items-center gap-1.5 mb-0.5">
          <span
            className="truncate text-[9px] font-bold uppercase tracking-[0.06em] leading-none"
            style={{ color }}
          >
            {typeLabel}
          </span>

          <span className="flex-1" />

          {/* INPUT_DATA "data available?" badge (owner #4) — distinct from the
              validation pin: a data glyph, green when a file is uploaded, amber
              when the node still needs one. */}
          {data.inputDataReady !== undefined && (
            <Database
              className={`w-3 h-3 flex-shrink-0 ${data.inputDataReady ? 'text-success' : 'text-warning'}`}
              strokeWidth={1.75}
              aria-label={data.inputDataReady ? 'Data uploaded' : 'Data required — no file uploaded'}
            />
          )}

          {status && (
            <span
              aria-label={status.label}
              title={status.label}
              className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${status.dotClass} ${status.pulse ? 'animate-pulse' : ''}`}
            />
          )}

          {hasValidationIssue && (
            <AlertCircle
              className="w-3 h-3 text-warning flex-shrink-0"
              strokeWidth={1.75}
              aria-label={validationLabel || 'Validation issue'}
            />
          )}
        </div>

        {/* Body — node title, single line with ellipsis (full name on hover) */}
        <div className="truncate text-[12px] leading-tight font-semibold text-fg">
          {label}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2 !h-2 !border-2 !border-surface !-bottom-1"
        style={{ backgroundColor: 'var(--border-strong)' }}
      />
    </div>
  );
}

export default memo(ModelingNodeComponent);
